import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell, { Field } from "@/components/AuthShell";
import { ErrorBox } from "@/components/Bits";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/login", form);
      await refreshUser();
      const next = params.get("suite");
      nav(next && next.startsWith("/") ? next : "/tableau-de-bord", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Connexion" subtitle="Retrouvez vos ligues et vos prédictions."
      footer={<>Pas encore de compte ? L'inscription se fait uniquement par <span className="text-white">lien d'invitation</span> reçu d'un ami.</>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Adresse courriel">
          <input className="mpl-input" type="email" required autoComplete="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        </Field>
        <Field label="Mot de passe">
          <input className="mpl-input" type="password" required autoComplete="current-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        </Field>
        {error && <ErrorBox message={error} />}
        <button className="mpl-btn-primary w-full py-3" disabled={busy} data-testid="login-submit-button">
          {busy ? "Connexion…" : "Se connecter"}
        </button>
        <Link to="/mot-de-passe-oublie" className="block text-center text-sm text-slate-400 hover:text-white" data-testid="forgot-password-link">
          Mot de passe oublié ?
        </Link>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Mot de passe oublié" subtitle="Entrez votre adresse : si un compte existe, vous recevrez un lien valide 1 heure."
      footer={<Link to="/connexion" className="text-white underline" data-testid="back-to-login-link">Retour à la connexion</Link>}>
      {msg ? (
        <p data-testid="forgot-success" className="mpl-card p-4 text-sm text-slate-200">{msg}</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <Field label="Adresse courriel">
            <input className="mpl-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          </Field>
          {error && <ErrorBox message={error} />}
          <button className="mpl-btn-primary w-full py-3" disabled={busy} data-testid="forgot-submit-button">Envoyer le lien</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      setDone(data.message);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <AuthShell title="Nouveau mot de passe" subtitle="Au moins 8 caractères.">
      {done ? (
        <div className="space-y-4">
          <p data-testid="reset-success" className="mpl-card p-4 text-sm">{done}</p>
          <Link to="/connexion" className="mpl-btn-primary w-full" data-testid="reset-login-link">Se connecter</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <Field label="Nouveau mot de passe">
            <input className="mpl-input" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
          </Field>
          {error && <ErrorBox message={error} />}
          <button className="mpl-btn-primary w-full py-3" data-testid="reset-submit-button">Enregistrer</button>
        </form>
      )}
    </AuthShell>
  );
}
